import React from 'react';

import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Checkbox, FormHelperText } from '@mui/material';
import { insertBetween } from '../../utils';

export interface MultiSelectOption<T> {
    label: React.ReactNode;
    value: T;
    disabled?: boolean;
}

export interface MultiSelectProps<T> {
    label: React.ReactNode;
    name: string;
    values: T[];
    options: MultiSelectOption<T>[];
    onChange: (values: T[]) => void;
    labelWidth?: number;
    disabled?: boolean;
    required?: boolean;
    helperText?: React.ReactNode;
}

interface MultiSelectState {
    opened: boolean;
}


export default class MultiSelect<T extends string | number> extends React.Component<MultiSelectProps<T>, MultiSelectState>{

    constructor(props: MultiSelectProps<T>) {
        super(props);
        this.state = {
            opened: false
        }
    }

    render() {
        const { label, name, values, options, onChange, labelWidth, disabled, required, helperText } = this.props;
        const { opened } = this.state;
        return (
            <FormControl
                fullWidth
                variant='outlined'
                margin='dense'
            >
                <InputLabel id={`${name}-label`}>{label}{required ? ' *' : ''}</InputLabel>
                <Select
                    variant='outlined'
                    margin='dense'
                    labelId={`${name}-label`}
                    name={name}
                    multiple
                    fullWidth
                    disabled={options.length === 0 || disabled}
                    value={values}
                    onChange={ev => onChange(ev.target.value as T[])}
                    required={required}
                    input={<OutlinedInput
                        name={name}
                        fullWidth
                        label={opened || values.length > 0 ? label : undefined}
                        notched
                        disabled={disabled}
                    />}
                    onFocus={() => this.setState({ opened: true })}
                    onBlur={() => this.setState({ opened: false })}
                    renderValue={(selected) => {
                        if (Array.isArray(selected)) {
                            const list = selected
                                .map(v => options.filter(o => o.value === v))
                                .map(([v]) => v).filter(v => v).map(v => v.label);
                            return insertBetween(list, ', ');
                        }
                        return null;
                    }}
                >
                    {options.map((option, index) => {
                        const selected = values.includes(option.value);
                        return (
                            <MenuItem disabled={option.disabled} selected={selected} style={{ padding: '0 4px' }} key={index} value={option.value}>
                                <Checkbox disabled={option.disabled} checked={selected} size='small' color='primary' />
                                <ListItemText primary={option.label} />
                            </MenuItem>
                        );
                    })}
                </Select>
                {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
            </FormControl>
        );
    }
}